// src/config/database/lfFinishingProcessesConfig.js

export const lfFinishingProcessesTableConfig = {
  // Table identification
  id: 'lf_finishing_processes',
  name: 'Large Format Finishing Processes',
  apiEndpoint: '/api/admin/large-format/finishing-processes',
  
  // Column definitions for the DataTable
  columns: [
    {
      key: 'name',
      label: 'Process Name',
      sortable: true,
      width: '25%',
      render: (value) => (
        <span className="font-medium text-gray-900">{value}</span>
      )
    },
    {
      key: 'setup_cost',
      label: 'Setup Cost',
      sortable: true,
      width: '15%',
      render: (value) => (
        <span className="text-green-600 font-medium">
          {value ? `£${parseFloat(value).toFixed(2)}` : '£0.00'}
        </span>
      )
    },
    {
      key: 'cost_per_sqm',
      label: 'Cost per m²',
      sortable: true,
      width: '15%',
      render: (value) => (
        <span className="text-blue-600 font-medium">
          {value ? `£${parseFloat(value).toFixed(2)}/m²` : '-'}
        </span>
      )
    },
    {
      key: 'cost_per_item',
      label: 'Cost per Item',
      sortable: true,
      width: '15%',
      render: (value) => (
        <span className="text-purple-600 font-medium">
          {value ? `£${parseFloat(value).toFixed(2)}/item` : '-'}
        </span>
      )
    },
    {
      key: 'cost_per_m_cut_length',
      label: 'Cost per Linear Meter',
      sortable: true,
      width: '15%',
      render: (value) => (
        <span className="text-orange-600 font-medium">
          {value ? `£${parseFloat(value).toFixed(2)}/m` : '-'}
        </span>
      )
    },
    {
      key: 'minimum_price',
      label: 'Minimum Price',
      sortable: true,
      width: '12%',
      render: (value) => (
        <span className="text-indigo-600 font-medium">
          {value ? `£${parseFloat(value).toFixed(2)} min` : '-'}
        </span>
      )
    },
    {
      key: 'pricing_config',
      label: 'Pricing Config',
      sortable: false,
      width: '20%',
      render: (value) => (
        <span className="text-xs text-gray-600">
          {value && Object.keys(value).length > 0 ? 'Custom JSON config' : '-'}
        </span>
      )
    },
    {
      key: 'updated_at',
      label: 'Last Modified',
      sortable: true,
      width: '10%',
      render: (value) => (
        <span className="text-gray-500 text-sm">
          {value ? new Date(value).toLocaleDateString() : '-'}
        </span>
      )
    }
  ],

  // Field definitions for the RecordModal form
  fields: [
    {
      key: 'name',
      label: 'Process Name',
      type: 'text',
      required: true,
      placeholder: 'e.g., Laminating, Cutting, Mounting',
      help: 'Descriptive name for this finishing process'
    },
    {
      key: 'setup_cost',
      label: 'Setup Cost (£)',
      type: 'number',
      required: false,
      step: 0.01,
      min: 0,
      defaultValue: '0',
      placeholder: '0.00',
      help: 'One-time setup cost for this process (e.g., machine setup, preparation)'
    },
    {
      key: 'cost_per_sqm',
      label: 'Cost per Square Meter (£)',
      type: 'number',
      required: false,
      step: 0.0001,
      min: 0,
      placeholder: '0.0000',
      help: 'Cost applied per square meter of material processed (leave empty if not applicable)'
    },
    {
      key: 'cost_per_item',
      label: 'Cost per Item (£)',
      type: 'number',
      required: false,
      step: 0.0001,
      min: 0,
      placeholder: '0.0000',
      help: 'Cost applied per individual item processed (leave empty if not applicable)'
    },
    {
      key: 'cost_per_m_cut_length',
      label: 'Cost per Linear Meter Cut (£)',
      type: 'number',
      required: false,
      step: 0.0001,
      min: 0,
      placeholder: '0.0000',
      help: 'Cost applied per linear meter of cutting (e.g., for contour cutting, leave empty if not applicable)'
    },
    {
      key: 'pricing_config',
      label: 'Pricing Config (JSON)',
      type: 'textarea',
      required: false,
      rows: 8,
      placeholder: '{\n  "pricing_method": "perimeter_tiered",\n  "area_basis": "single_banner_sqm",\n  "threshold_sqm": 7.5,\n  "rate_below_or_equal_per_m": 2.0,\n  "rate_above_per_m": 3.0\n}',
      help: 'Optional finish-specific pricing rule. For banner perimeter pricing use perimeter_tiered with a single-banner sqm threshold and lower/higher per-meter rates.'
    },
    {
      key: 'allow_volume_discount',
      label: 'Allow Volume Discount',
      type: 'checkbox',
      required: false,
      defaultValue: false,
      help: 'When enabled, this finish\'s run-on charges and material surcharges can be reduced by the large-format volume discount. Setup and minimum-price uplifts stay undiscounted.'
    },
    {
      key: 'minimum_price',
      label: 'Minimum Price (£)',
      type: 'number',
      required: false,
      step: 0.01,
      min: 0,
      placeholder: '0.00',
      help: 'Minimum price for this process regardless of calculated cost (e.g., for small jobs with labour-intensive work)'
    }
  ]
};

// Transform functions for API communication
export const transformLfFinishingProcessForApi = (formData) => {
  const transformed = {
    name: formData.name,
    setup_cost: formData.setup_cost ? parseFloat(formData.setup_cost) : 0,
    allow_volume_discount: Boolean(formData.allow_volume_discount)
  };

  // Only include cost fields if they have values
  if (formData.cost_per_sqm && formData.cost_per_sqm !== '' && parseFloat(formData.cost_per_sqm) > 0) {
    transformed.cost_per_sqm = parseFloat(formData.cost_per_sqm);
  }

  if (formData.cost_per_item && formData.cost_per_item !== '' && parseFloat(formData.cost_per_item) > 0) {
    transformed.cost_per_item = parseFloat(formData.cost_per_item);
  }

  if (formData.cost_per_m_cut_length && formData.cost_per_m_cut_length !== '' && parseFloat(formData.cost_per_m_cut_length) > 0) {
    transformed.cost_per_m_cut_length = parseFloat(formData.cost_per_m_cut_length);
  }

  if (formData.minimum_price && formData.minimum_price !== '' && parseFloat(formData.minimum_price) > 0) {
    transformed.minimum_price = parseFloat(formData.minimum_price);
  }

  if (formData.pricing_config && formData.pricing_config.trim() !== '') {
    transformed.pricing_config = JSON.parse(formData.pricing_config);
  }

  return transformed;
};

export const transformLfFinishingProcessFromApi = (apiData) => {
  return {
    id: apiData.id,
    name: apiData.name || '',
    setup_cost: apiData.setup_cost ? apiData.setup_cost.toString() : '0',
    cost_per_sqm: apiData.cost_per_sqm ? apiData.cost_per_sqm.toString() : '',
    cost_per_item: apiData.cost_per_item ? apiData.cost_per_item.toString() : '',
    cost_per_m_cut_length: apiData.cost_per_m_cut_length ? apiData.cost_per_m_cut_length.toString() : '',
    pricing_config: apiData.pricing_config ? JSON.stringify(apiData.pricing_config, null, 2) : '',
    allow_volume_discount: Boolean(apiData.allow_volume_discount),
    minimum_price: apiData.minimum_price ? apiData.minimum_price.toString() : ''
  };
};

// Validation rules
export const validateLfFinishingProcess = (data) => {
  const errors = {};
  
  if (!data.name || data.name.trim() === '') {
    errors.name = 'Process name is required';
  }
  
  // Validate numeric fields
  const numericFields = ['setup_cost', 'cost_per_sqm', 'cost_per_item', 'cost_per_m_cut_length', 'minimum_price'];

  numericFields.forEach(field => {
    if (data[field] && data[field] !== '') {
      const value = parseFloat(data[field]);
      if (isNaN(value) || value < 0) {
        errors[field] = `${field.replace(/_/g, ' ')} must be a non-negative number`;
      }
    }
  });
  
  // Check that at least one cost type is specified (setup cost always counts)
  const hasSetupCost = data.setup_cost && parseFloat(data.setup_cost) > 0;
  const hasSqmCost = data.cost_per_sqm && parseFloat(data.cost_per_sqm) > 0;
  const hasItemCost = data.cost_per_item && parseFloat(data.cost_per_item) > 0;
  const hasCutCost = data.cost_per_m_cut_length && parseFloat(data.cost_per_m_cut_length) > 0;
  
  if (!hasSetupCost && !hasSqmCost && !hasItemCost && !hasCutCost) {
    errors.setup_cost = 'At least one cost type must be specified (setup, per m², per item, or per linear meter)';
  }

  if (data.pricing_config && data.pricing_config.trim() !== '') {
    try {
      const parsed = JSON.parse(data.pricing_config);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        errors.pricing_config = 'Pricing config must be a JSON object';
      }
    } catch (error) {
      errors.pricing_config = 'Invalid JSON format';
    }
  }
  
  return Object.keys(errors).length > 0 ? errors : null;
};

// Helper function to get cost summary for display
export const getCostSummary = (process) => {
  const costs = [];
  
  if (process.setup_cost && process.setup_cost > 0) {
    costs.push(`Setup: £${parseFloat(process.setup_cost).toFixed(2)}`);
  }
  
  if (process.cost_per_sqm && process.cost_per_sqm > 0) {
    costs.push(`£${parseFloat(process.cost_per_sqm).toFixed(2)}/m²`);
  }
  
  if (process.cost_per_item && process.cost_per_item > 0) {
    costs.push(`£${parseFloat(process.cost_per_item).toFixed(2)}/item`);
  }
  
  if (process.cost_per_m_cut_length && process.cost_per_m_cut_length > 0) {
    costs.push(`£${parseFloat(process.cost_per_m_cut_length).toFixed(2)}/m cut`);
  }

  if (process.minimum_price && process.minimum_price > 0) {
    costs.push(`Min: £${parseFloat(process.minimum_price).toFixed(2)}`);
  }

  if (process.pricing_config) {
    costs.push('Custom pricing config');
  }

  return costs.length > 0 ? costs.join(', ') : 'No costs defined';
};

// Common finishing process templates for quick creation
export const FINISHING_PROCESS_TEMPLATES = [
  {
    name: 'Laminating',
    setup_cost: '5.00',
    cost_per_sqm: '2.50',
    cost_per_item: '',
    cost_per_m_cut_length: ''
  },
  {
    name: 'Contour Cutting',
    setup_cost: '3.00',
    cost_per_sqm: '',
    cost_per_item: '',
    cost_per_m_cut_length: '0.50'
  },
  {
    name: 'Mounting',
    setup_cost: '2.00',
    cost_per_sqm: '1.50',
    cost_per_item: '',
    cost_per_m_cut_length: ''
  },
  {
    name: 'Hemming',
    setup_cost: '1.00',
    cost_per_sqm: '',
    cost_per_item: '',
    cost_per_m_cut_length: '0.75'
  },
  {
    name: 'Eyeletting',
    setup_cost: '1.50',
    cost_per_sqm: '',
    cost_per_item: '0.25',
    cost_per_m_cut_length: ''
  }
];

export default lfFinishingProcessesTableConfig;
